// /src/components/UI/ConfigByChips/ConfigByChips

/**
 * The ConfigsByChips component provides a flexible and interactive way
 * to manage a dynamic list of config values (represented as chips)
 * in the RAVEN application
 */

import React, { useState, useRef } from "react";
import { useUpdateEffect } from "react-use";

import ChipsInput from "./ChipsInput";

// Custom hooks
import { useDialogBox } from "src/hooks/useDialogBox";

// Layout/UI
import { Box, Card, Icon, MetreaTooltip } from "@metrea-ui/v2";
import ManageChip from "./ManageChip/ManageChip";
import styles from "./ConfigsByChips.module.css";
import chipStyles from "./ChipsInput.module.css";
import { DismissRegular } from "@fluentui/react-icons";

// Utilities
import { mColor, getContrastYIQ } from "src/utils/themeUtils";
import { getRandomInteger, getValuesByPropertyPath, deepClone } from "src/utils/dataUtils";

const ConfigsByChips = (props) => {
	const renderCount = useRef(0);
	renderCount.current++;

	const isEditMode = props.mode === "edit";

	const initialConfig = deepClone(props.item, []);
	const [localConfig, setLocalConfig] = useState(initialConfig);
	const [chips, setChips] = useState(getValuesByPropertyPath(initialConfig, "comp_value") || []);

	const [newChipAdded, setNewChipAdded] = useState(false);

	const { showDialog } = useDialogBox();

	useUpdateEffect(() => {
		const updated = deepClone(props.item, []);
		const newChips = getValuesByPropertyPath(updated, "comp_value") || [];

		// Compare newChips and current chips to avoid unnecessary overwrite
		if (JSON.stringify(newChips) !== JSON.stringify(chips)) {
			setLocalConfig(updated);
			setChips(updated.filter((cfg) => cfg.update_flag !== 103).map((cfg) => cfg.comp_value));
			setNewChipAdded(false);
		}
	}, [props.item]);

	const handleChange = (newChips) => {
		const chipsToApply = Array.isArray(newChips) ? newChips : [];
		const currentConfig = Array.isArray(localConfig) ? localConfig : [];
		const uniqueElements = [...new Set(chipsToApply)];
		if (chipsToApply.length !== uniqueElements.length) {
			showDialog({
				title: "Error",
				message: ["The data is not unique!"],
				type: "error",
			});
			// newChips.pop();
			return;
		}

		// Clone current config for immutability
		const newConfig = deepClone(currentConfig, []);

		// let updateDbNow = false;
		if (chipsToApply.length === currentConfig.length) {
			// debugLog(10, "[ConfigsByChips] handleChange #1", { newChips: newChips });

			// Update configs with newChips
			newConfig.forEach((cfg, index) => {
				if (cfg.comp_value !== chipsToApply[index]) {
					cfg.comp_value = chipsToApply[index];
					cfg.update_flag = 102;
				}
			});
		} else if (chipsToApply.length > currentConfig.length) {
			// debugLog(10, "[ConfigsByChips] handleChange #2", { newChips: newChips });

			/* NOTE: keep it for the reference
				comp_value: newChips.slice(-1).pop();  // last chip

				we can use this to get the last chip, assuming
				- The array is always updated with the new chip at the end.
				- Only one new chip is added at a time.
				- There are no duplicates.
			*/

			// Add configs if found anything new in newChips
			const newValue = chipsToApply.find((val) => !currentConfig.some((cfg) => cfg.comp_value === val));

			if (newValue) {
				newConfig.push({
					id: getRandomInteger(1000000000, 2000000000),
					baseline_id: props.baselineId,
					comp_type_id: props.type.id,
					comp_value: newValue,
					update_flag: 101,
				});
				setNewChipAdded(true);
			}
		} else {
			// debugLog(10, "[ConfigsByChips] handleChange #3", { newChips: newChips });

			/* NOTE: keep it for the reference
				const newArray = newConfig.filter((cfg, index) => {
					return newChips.indexOf(cfg.comp_value) > -1;
				});
				newConfig.length = 0;
				newArray.map((cfg) => {
					newConfig.push(cfg);
					return true;
				});
				// // no effect, since all cfg elements not in the newChips were removed already
				// newConfig.forEach((cfg) => {
				// 	if (!newChips.includes(cfg.comp_value)) {
				// 		cfg.update_flag = 103; // Mark for deletion
				// 	}
				// });
			*/

			// const currentChipValues = getValuesByPropertyPath(localConfig, "comp_value");
			// const deletedValues = currentChipValues.filter((val) => !newChips.includes(val));
			// // update the removed item with update_flag = 103
			// newConfig.forEach((cfg) => {
			// 	if (deletedValues.includes(cfg.comp_value)) {
			// 		cfg.update_flag = 103;
			// 	}
			// });

			// for (let i = newConfig.length - 1; i >= 0; i--) {
			// 	if (!newChips.includes(newConfig[i].comp_value)) {
			// 		newConfig.splice(i, 1);
			// 	}
			// }

			// Remove configs not in newChips
			for (let i = newConfig.length - 1; i >= 0; i--) {
				const value = newConfig[i].comp_value;
				if (!chipsToApply.includes(value)) {
					newConfig.splice(i, 1);
				}
			}
		}

		// Update local state
		setLocalConfig(newConfig);

		// Filter for visible chips (excluding deleted)
		const visibleChips = newConfig.filter((cfg) => cfg.update_flag !== 103).map((cfg) => cfg.comp_value);
		setChips(visibleChips);

		// 🔁 Propagate update
		props.onUpdate(newConfig, `cfg_${props.type.code}`, props.type.id);

		// debugLog(10, "[ConfigsByChips] handleChange #last", {
		// 	visibleChips: visibleChips,
		// 	newConfig: newConfig,
		// 	cfg: `cfg_${props.type.code}`,
		// });

		// ATTENTION!!  onClearComplete HAVE TO be here to work - ylee
		props.onClearComplete();
	};

	const chooseChipHandler = (chip, index) => {
		if (props.dataChanged && newChipAdded) {
			showDialog({
				title: "Information",
				message: [`It's a newly added element. Please save the baseline to continue.`],
			});
			return;
		}

		const thisCfg = localConfig?.[index];
		if (!thisCfg) return; // safety guard

		thisCfg.typeName = props.type.name;

		setChipModalOpen(thisCfg);
	};

	const [chipModalOpen, setChipModalOpen] = useState(false);
	const chipModalOpenHandler = (open) => {
		setChipModalOpen(open);
	};
	const handleClearComplete = () => {
		props.onClearComplete();
	};

	const saveChipHandler = (thisCfg) => {
		// debugLog(10, "[ConfigsByChips] saveChipHandler", {
		// 	thisCfg: thisCfg,
		// });

		setChips(getValuesByPropertyPath(thisCfg, "comp_value"));
		setLocalConfig(thisCfg);
		props.onUpdate(thisCfg, `cfg_${props.type.code}`, props.type.id);
	};

	// debugLog(10, `[ConfigsByChips] (renderCount: ${renderCount.current})`, {
	// 	props: props,
	// 	localConfig: localConfig,
	// 	chips: chips,
	// });
	const invalid = Boolean(props.isInvalid ?? props.error);
	const helperContent =
		(typeof props.error === "string" && props.error.trim()) || props.errorText || props.helperText || "";

	return (
		<>
			{chipModalOpen && (
				<ManageChip
					title={`${chipModalOpen.typeName} Management`}
					// description="This is used for Chips Management"
					item={chipModalOpen}
					list={localConfig}
					onSetOpen={chipModalOpenHandler}
					onClearComplete={handleClearComplete}
					onSave={saveChipHandler}
				/>
			)}

			{/* Use the same neutral shell as period inputs so Step 2 sections read consistently. */}
			<Card.Root variant="elevated">
				<Card.Body padding="1rem">
					<Box as="form" marginY={0} noValidate autoComplete="off">
						<div
							className={styles.configLabel}
							style={invalid ? { color: mColor.Danger } : undefined}
						>
							{props.type.name}
							{props.required ? (
								<span style={{ color: mColor.Danger, marginLeft: 4 }}>*</span>
							) : null}
						</div>
						{helperContent ? (
							<div
								style={{
									paddingLeft: "10px",
									paddingBottom: "10px",
									color: invalid ? mColor.Danger : "#6b7280",
									fontSize: "0.8rem",
								}}
							>
								{helperContent}
							</div>
						) : null}

						<div className={styles.configGroup}>
							<ChipsInput
								value={chips}
								disabled={!isEditMode}
								onChange={handleChange}
								placeholder={
									isEditMode ? "To add a new item: start typing and press enter..." : ""
								}
								renderChip={({ chip, index, onDelete }) => {
									const cfg = localConfig?.[index];
									const bgColor =
										cfg?.color && cfg.color !== "null" ? cfg.color : "#f2f2f2";
									const fontColor = bgColor
										? getContrastYIQ(bgColor)
											? mColor.MidnightBlue
											: mColor.White
										: mColor.MidnightBlue;
									const chipNode = (
										<span
											key={`${chip}-${index}`}
											className={chipStyles.chip}
											onDoubleClick={() => chooseChipHandler(chip, index)}
											style={{
												backgroundColor: bgColor,
												color: fontColor,
												cursor: isEditMode ? "pointer" : "none",
											}}
										>
											{chip}

											{isEditMode && (
												<MetreaTooltip
													content={`Delete "${cfg?.comp_value ?? "chip"}"`}
												>
													<Icon
														as={DismissRegular}
														boxSize="0.9rem"
														aria-label={`Delete ${cfg?.comp_value ?? "chip"}`}
														style={{ color: fontColor }}
														onClick={(e) => {
															e.stopPropagation();
															onDelete();
														}}
													/>
												</MetreaTooltip>
											)}
										</span>
									);

									// In edit mode, the chip body advertises the double-click affordance while
									// the delete icon keeps its own narrower tooltip for the destructive action.
									return isEditMode ? (
										<MetreaTooltip
											key={`${chip}-${index}`}
											content={`Double click to manage "${cfg?.comp_value ?? "chip"}" details`}
										>
											{chipNode}
										</MetreaTooltip>
									) : (
										chipNode
									);
								}}
							/>
						</div>
					</Box>
				</Card.Body>
			</Card.Root>
		</>
	);
};

export default ConfigsByChips;
